/**
 * InviteUserDialog Component (TASK-049)
 *
 * Dialog for inviting new users with email input,
 * role selection, and permission preview.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usersApi } from '@/services/api/users';
import type { UserRole, InviteUserPayload } from '@/types/user';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_PERMISSION_PREVIEW: Record<UserRole, string[]> = {
  OWNER: [
    'Полный доступ ко всем разделам системы',
    'Управление пользователями и настройками организации',
  ],
  USER: [
    'Создание смет и просмотр данных',
    'Ограниченное управление пользователями и организацией',
  ],
};

export function InviteUserDialog({
  open,
  onClose,
  onSuccess,
}: InviteUserDialogProps): JSX.Element | null {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('USER');
  const [sendInvitation, setSendInvitation] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setEmail('');
      setRole('USER');
      setSendInvitation(true);
      setEmailError(null);
      setFormError(null);
      // Focus email input on open
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: (payload: InviteUserPayload) => usersApi.invite(payload),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: unknown) => {
      // Check for specific field errors
      const apiError = error as { response?: { data?: { email?: string[] } } };
      if (apiError.response?.data?.email) {
        setEmailError(apiError.response.data.email[0]);
      } else if (error instanceof Error) {
        setFormError(error.message);
      } else {
        setFormError('Ошибка при отправке приглашения');
      }
    },
  });

  // Validate email
  const validateEmail = useCallback((value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email обязателен');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Некорректный email адрес');
      return false;
    }
    setEmailError(null);
    return true;
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setFormError(null);

      if (!validateEmail(email)) {
        return;
      }

      inviteMutation.mutate({
        email: email.trim(),
        role,
        send_invitation: sendInvitation,
      });
    },
    [email, role, sendInvitation, validateEmail, inviteMutation]
  );

  // Handle email change
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      if (emailError) {
        validateEmail(e.target.value);
      }
    },
    [emailError, validateEmail]
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        data-testid="invite-user-dialog"
        aria-labelledby="invite-dialog-title"
        aria-modal="true"
      >
        <DialogHeader>
          <DialogTitle id="invite-dialog-title">Пригласить пользователя</DialogTitle>
          <DialogDescription>
            Отправьте приглашение новому члену команды
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Form Error */}
          {formError && (
            <div
              data-testid="form-error"
              className="bg-red-50 text-red-800 px-4 py-3 rounded-lg border border-red-200 text-sm"
            >
              {formError}
            </div>
          )}

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              ref={emailInputRef}
              id="email"
              type="email"
              data-testid="email-input"
              value={email}
              onChange={handleEmailChange}
              placeholder="user@example.com"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'email-error' : undefined}
            />
            {emailError && (
              <p
                id="email-error"
                data-testid="email-error"
                className="text-sm text-red-600"
              >
                {emailError}
              </p>
            )}
          </div>

          {/* Role Field */}
          <div className="space-y-2">
            <Label htmlFor="role-select">Роль</Label>
            <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
              <SelectTrigger id="role-select" data-testid="role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USER">Пользователь</SelectItem>
                <SelectItem value="OWNER">Владелец</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Permission Preview */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-1">
            <p className="text-sm font-medium">Превью прав</p>
            <ul className="text-sm text-gray-700 space-y-1">
              {ROLE_PERMISSION_PREVIEW[role].map((permission) => (
                <li key={permission}>• {permission}</li>
              ))}
            </ul>
          </div>

          {/* Send Invitation Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="send-invitation"
              data-testid="send-invitation-checkbox"
              checked={sendInvitation}
              onCheckedChange={(checked) =>
                setSendInvitation(checked as boolean)
              }
            />
            <Label
              htmlFor="send-invitation"
              className="text-sm font-normal cursor-pointer"
            >
              Отправить приглашение по email
            </Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="cancel-button"
              onClick={handleCancel}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending && (
                <Loader2
                  data-testid="submit-loading"
                  className="mr-2 h-4 w-4 animate-spin"
                />
              )}
              {inviteMutation.isPending ? 'Отправка...' : 'Отправить приглашение'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InviteUserDialog;
